import React, { useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { Eye, EyeOff } from 'lucide-react'
import Loader from '../../base-component/Loader/Loader'
import { changePasswordApi } from '../../Services/ApiServices'

const changePasswordSchema = Yup.object().shape({
    currentPassword: Yup.string()
        .required('Current password is required')
        .min(6, 'Current password must be at least 6 characters'),
    newPassword: Yup.string()
        .required('New password is required')
        .min(8, 'New password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
            'New password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),
    confirmPassword: Yup.string()
        .required('Please confirm your new password')
        .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
})

export default function ChangePassword() {
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    })

    const initialValues = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    }

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        console.log("Password change data:", values);
        setSubmitting(true);
        changePasswordApi({
            oldPassword: values.currentPassword,
            newPassword: values.newPassword,
        })
        .then(res => {
            console.log(res.data);
            toast.success('Password changed successfully');
            resetForm();
        })
        .catch(err => {
            console.log(err);
            toast.error('Failed to change password');
        })
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }))
    }

    return (
        <div className=""> 
            <div className="flex flex-col items-start mb-8">
                <h1 className="md:text-xl text-lg font-bold mb-2">Change Password</h1>
                <p className="text-start text-sm text-gray-600">
                    Enter your current password and choose a new secure password
                </p>
            </div>

            <Formik
                initialValues={initialValues}
                validationSchema={changePasswordSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form className="mt-8 w-full space-y-6">
                        <div className="space-y-4 w-full">
                            <div>
                                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    Current Password
                                </label>
                                <div className="relative w-full">
                                    <Field
                                        type={showPasswords.current ? "text" : "password"}
                                        id="currentPassword"
                                        name="currentPassword"
                                        placeholder="Enter your current password"
                                        className={`w-full px-3 h-12 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-[#00987C] ${errors.currentPassword && touched.currentPassword
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => togglePasswordVisibility('current')}
                                    >
                                        {!showPasswords.current ? (
                                            <EyeOff className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                <ErrorMessage name="currentPassword" component="div" className="mt-1 text-sm text-red-600" />
                            </div>

                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    New Password
                                </label>
                                <div className="relative w-full">
                                    <Field
                                        type={showPasswords.new ? "text" : "password"}
                                        id="newPassword"
                                        name="newPassword"
                                        placeholder="Enter your new password"
                                        className={`w-full px-3 h-12 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-[#00987C] ${errors.newPassword && touched.newPassword
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => togglePasswordVisibility('new')}
                                    >
                                        {!showPasswords.new ? (
                                            <EyeOff className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                <ErrorMessage name="newPassword" component="div" className="mt-1 text-sm text-red-600" />
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirm New Password
                                </label>
                                <div className="relative w-full">
                                    <Field
                                        type={showPasswords.confirm ? "text" : "password"}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        placeholder="Confirm your new password"
                                        className={`w-full px-3 h-12 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-[#00987C] ${errors.confirmPassword && touched.confirmPassword
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => togglePasswordVisibility('confirm')}
                                    >
                                        {!showPasswords.confirm ? (
                                            <EyeOff className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                <ErrorMessage name="confirmPassword" component="div" className="mt-1 text-sm text-red-600" />
                            </div>
                        </div>

                        <div className='flex justify-end w-full'>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="group relative md:w-48 w-full flex justify-center h-12 items-center px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#2C5BA7] focus:outline-none focus:ring-none disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <Loader />
                                ) : (
                                    'Change Password'
                                )}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    )
}
